import React from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { StatusPill } from "@/components/StatusPill";
import { timeAgo } from "@/lib/format";
import { MapPin, Eye, Send, AlertTriangle, CalendarClock, ChevronRight, Trash2, CheckCircle2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

const SWIPE_TRIGGER = 96;

function dueLabel(iso) {
  const d = new Date(iso + "T00:00:00");
  const today = new Date(new Date().toDateString());
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0) return "overdue";
  if (diff === 0) return "due today";
  if (diff === 1) return "due tmrw";
  if (diff < 7) return `due ${d.toLocaleDateString(undefined, { weekday: "short" })}`;
  return `due ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

function Row({ job, onOpen, onDelete, onToggle }) {
  const dragged = React.useRef(false);
  const x = useMotionValue(0);
  const completeOpacity = useTransform(x, [-SWIPE_TRIGGER, -24, 0], [1, 0.45, 0]);
  const deleteOpacity = useTransform(x, [0, 24, SWIPE_TRIGGER], [0, 0.45, 1]);
  const completed = job.status === "completed";
  const handleOpen = () => { if (dragged.current) return; onOpen && onOpen(); };

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Delete — drag RIGHT */}
      <motion.div style={{ opacity: deleteOpacity }} className="absolute inset-y-0 left-0 z-0 flex w-1/2 items-center justify-start bg-rose-500 pl-5 text-white" data-testid={`job-swipe-delete-${job.id}`}>
        <div className="flex flex-col items-center gap-1"><Trash2 className="h-5 w-5" /><span className="text-[11px] font-semibold">Delete</span></div>
      </motion.div>
      {/* Complete / Reopen — drag LEFT */}
      <motion.div style={{ opacity: completeOpacity }} className="absolute inset-y-0 right-0 z-0 flex w-1/2 items-center justify-end bg-emerald-500 pr-5 text-white" data-testid={`job-swipe-complete-${job.id}`}>
        <div className="flex flex-col items-center gap-1">{completed ? <RotateCcw className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}<span className="text-[11px] font-semibold">{completed ? "Reopen" : "Complete"}</span></div>
      </motion.div>

      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.9}
        whileTap={{ scale: 0.97 }}
        style={{ x }}
        onDragStart={() => { dragged.current = true; }}
        onDragEnd={(e, info) => {
          if (info.offset.x <= -SWIPE_TRIGGER) onToggle && onToggle();
          else if (info.offset.x >= SWIPE_TRIGGER) onDelete && onDelete();
          setTimeout(() => { dragged.current = false; }, 60);
        }}
        role="button"
        tabIndex={0}
        data-testid={`job-card-${job.id}`}
        onClick={handleOpen}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleOpen()}
        className="card-elevated relative z-10 flex w-full touch-pan-y cursor-pointer items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold">{job.name}</span>
          </div>
          {job.location && (
            <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="h-3 w-3" /> <span className="truncate">{job.location}</span>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusPill status={job.status} />
            {job.latest_report_status && <StatusPill status={job.latest_report_status} />}
            {job.total_views > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground" data-testid={`views-badge-${job.id}`}>
                <Eye className="h-3 w-3" /> {job.total_views}
              </span>
            )}
            {job.dispatched_count > 0 && (() => {
              const overdue = job.overdue_count || 0;
              const awaiting = job.awaiting_count || 0;
              const label = overdue > 0
                ? `${job.dispatched_count} sent · ${overdue} overdue`
                : awaiting > 0
                  ? `${job.dispatched_count} sent · ${awaiting} awaiting`
                  : `${job.dispatched_count} sent · all back`;
              const cls = overdue > 0
                ? "bg-amber-500/15 text-amber-600 dark:text-amber-300"
                : "bg-sky-500/15 text-sky-600 dark:text-sky-300";
              return (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid={`awaiting-badge-${job.id}`}>
                  {overdue > 0 ? <AlertTriangle className="h-3 w-3" /> : <Send className="h-3 w-3" />}
                  {label}
                </span>
              );
            })()}
            {job.next_due && (job.overdue_count || 0) === 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground" data-testid={`due-badge-${job.id}`}>
                <CalendarClock className="h-3 w-3" /> {dueLabel(job.next_due)}
              </span>
            )}
            {job.dispatched_count > 0 ? (
              job.last_sent_at && <span className="text-xs text-muted-foreground">Sent {timeAgo(job.last_sent_at)}</span>
            ) : (
              job.latest_report_at && <span className="text-xs text-muted-foreground">{timeAgo(job.latest_report_at)}</span>
            )}
          </div>
          {job.report_count > 0 && (() => {
            const green = job.progress || 0;
            const red = Math.min(100 - green, job.issue_pct || 0);
            const orange = Math.min(100 - green - red, job.review_pct || 0);
            const combined = Math.min(100, green + orange + red);
            const labelCls = red > 0 ? "text-rose-500" : green === 0 && orange > 0 ? "text-amber-500" : "text-emerald-600";
            return (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div className="h-full bg-emerald-500" style={{ width: `${green}%` }} />
                  <div className="h-full bg-amber-500" style={{ width: `${orange}%` }} />
                  <div className="h-full bg-rose-500" style={{ width: `${red}%` }} />
                </div>
                <span className={`text-xs font-bold ${labelCls}`} data-testid={`job-progress-pct-${job.id}`}>{combined}%</span>
              </div>
            );
          })()}
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/60" />
      </motion.div>
    </div>
  );
}

export const JobCard = React.memo(Row);
